/* global BigInt */
import { parseMathExpression, previewMathExpression } from "../components/inputs/mathExpression";

const abs = n => n < 0n ? -n : n;
function gcd(a, b) { a = abs(a); b = abs(b); while (b) [a, b] = [b, a % b]; return a || 1n; }
function rational(num, den = 1n) {
  if (!den) throw Error("zero denominator");
  const d = gcd(num, den), sign = den < 0n ? -1n : 1n;
  return { num: sign * num / d, den: sign * den / d };
}
const mul = (a, b) => rational(a.num * b.num, a.den * b.den);
const add = (a, b) => rational(a.num * b.den + b.num * a.den, a.den * b.den);
const constant = a => new Map(a.num ? [[1, a]] : []);
function merge(a, b) {
  const result = new Map(a);
  for (const [r, c] of b) { const next = add(result.get(r) || rational(0n), c); if (next.num) result.set(r, next); else result.delete(r); }
  return result;
}
function simplifyRoot(number) {
  if (number < 0n || number > 1000000000n) throw Error("root out of range");
  let remaining = number, coefficient = 1n, radicand = 1n;
  if (!number) return { coefficient: 0n, radicand: 1 };
  for (let p = 2n; p * p <= remaining; p++) {
    let count = 0;
    while (remaining % p === 0n) { remaining /= p; count++; }
    coefficient *= p ** BigInt(Math.floor(count / 2));
    if (count % 2) radicand *= p;
  }
  radicand *= remaining;
  return { coefficient, radicand: Number(radicand) };
}
function product(a, b) {
  let result = new Map();
  for (const [r, x] of a) for (const [s, y] of b) {
    const root = simplifyRoot(BigInt(r) * BigInt(s));
    result = merge(result, new Map([[root.radicand, mul(mul(x, y), rational(root.coefficient))]]));
  }
  return result;
}
function divide(a, b) {
  if (b.size !== 1) throw Error("invalid denominator");
  const [[r, c]] = b;
  // Rationalize a single radical denominator exactly.
  return product(a, new Map([[r, rational(c.den, c.num * BigInt(r))]]));
}
function scalar(value) {
  if (!value.size) return rational(0n);
  if (value.size !== 1 || !value.has(1)) throw Error("not rational");
  return value.get(1);
}
function evaluate(node) {
  if (node.k === "number") {
    const parts = node.a.split(".");
    return constant(rational(BigInt((parts[0] || "0") + (parts[1] || "")), 10n ** BigInt((parts[1] || "").length)));
  }
  if (node.k === "group" || node.k === "u+") return evaluate(node.a);
  if (node.k === "u-") return product(constant(rational(-1n)), evaluate(node.a));
  if (node.k === "+" || node.k === "mixed") return merge(evaluate(node.a), evaluate(node.b));
  if (node.k === "-") return merge(evaluate(node.a), product(constant(rational(-1n)), evaluate(node.b)));
  if (["*", "implicit"].includes(node.k)) return product(evaluate(node.a), evaluate(node.b));
  if (node.k === "/") return divide(evaluate(node.a), evaluate(node.b));
  if (node.k === "^") {
    const exponent = scalar(evaluate(node.b));
    if (exponent.den !== 1n || abs(exponent.num) > 8n) throw Error("invalid power");
    let result = constant(rational(1n));
    const base = evaluate(node.a);
    for (let i = 0n; i < abs(exponent.num); i++) result = product(result, base);
    return exponent.num < 0n ? divide(constant(rational(1n)), result) : result;
  }
  if (node.k === "sqrt") {
    const value = scalar(evaluate(node.a));
    const root = simplifyRoot(value.num * value.den);
    return product(constant(rational(root.coefficient, value.den)), new Map([[root.radicand, rational(1n)]]));
  }
  if (node.k === "cbrt") {
    const value = scalar(evaluate(node.a));
    const n = BigInt(Math.round(Math.cbrt(Number(value.num)))), d = BigInt(Math.round(Math.cbrt(Number(value.den))));
    if (n ** 3n !== value.num || d ** 3n !== value.den) throw Error("irrational cube root");
    return constant(rational(n, d));
  }
  throw Error("unsupported expression");
}

export function normalizeExactAnswer(answer) {
  let raw = typeof answer === "string" || typeof answer === "number" ? String(answer) : String(answer?.raw ?? "");
  if (raw.length > 1000) return "";
  raw = raw.replace(/\\left|\\right/g, "").replace(/\\,|\\!|\\;|\\quad/g, " ").replace(/[\u00a0\u202f\u200b]/g, " ");
  // Convert nested LaTeX fractions without changing root grouping.
  function convert(text) {
    let output = "";
    for (let i = 0; i < text.length;) {
      const command = /^\\(?:dfrac|tfrac|frac)\s*/.exec(text.slice(i));
      if (!command) { output += text[i++]; continue; }
      i += command[0].length;
      const read = () => {
        while (/\s/.test(text[i] || "") && i < text.length) i++;
        if (text[i] !== "{") throw Error("invalid fraction");
        const start = ++i; let depth = 1;
        while (i < text.length && depth) { if (text[i] === "{") depth++; if (text[i] === "}") depth--; i++; }
        if (depth) throw Error("invalid fraction");
        return convert(text.slice(start, i - 1));
      };
      const numerator = read(), denominator = read();
      output += "((" + numerator + ")/(" + denominator + "))";
    }
    return output;
  }
  try { return convert(raw); } catch { return ""; }
}

export function checkExactMathAnswer(answer, expected, radicand = 1) {
  try {
    const tree = parseMathExpression(normalizeExactAnswer(answer));
    if (!tree) return false;
    const actual = evaluate(tree);
    const root = simplifyRoot(BigInt(radicand));
    const coefficient = mul(rational(BigInt(expected.num), BigInt(expected.den)), rational(root.coefficient));
    const target = coefficient.num ? new Map([[root.radicand, coefficient]]) : new Map();
    return actual.size === target.size && [...target].every(([r, c]) => actual.get(r)?.num === c.num && actual.get(r)?.den === c.den);
  } catch { return false; }
}

export function exactAnswerPreview(answer, label = "") {
  const preview = previewMathExpression(normalizeExactAnswer(answer));
  return preview ? (label ? label + " = " + preview : preview) : null;
}
